import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversation } from '@/contexts/ConversationContext';
import { useDarkMode } from '@/contexts/DarkModeContext';
import { formatDistanceToNow } from 'date-fns';

function ConversationList({ isCollapsed }) {
  const { conversations, currentSessionId, startNewConversation, switchToConversation, updateConversationTitle } = useConversation();
  const activeConversation = conversations.find(conv => conv.id === currentSessionId);
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const cancelRef = useRef(false);
  
  const handleStartNewConversation = async () => {
    await startNewConversation();
    navigate('/ai-assistant');
  };
  
  const handleSwitchToConversation = async (sessionId) => {
    await switchToConversation(sessionId);
    navigate('/ai-assistant');
  };
  
  const startEditing = (id, title) => {
    setEditingId(id);
    setEditTitle(title);
  };
  
  const handleTitleChange = (e) => {
    setEditTitle(e.target.value);
  };
  
  const submitTitleEdit = async (id) => {
    if (editTitle.trim()) {
      await updateConversationTitle(id, editTitle.trim());
    }
    setEditingId(null);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };
  
  if (isCollapsed) {
    return (
      <div className="mt-4 px-2">
        <button
          onClick={handleStartNewConversation}
          className="w-full flex justify-center p-2 rounded-2xl bg-blue-50 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-gray-600"
          title="New Conversation"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    );
  }
  
  return (
    <div className="mt-8 px-4">
      <div className="mb-6">
        <h3 className="text-sm font-medium uppercase text-gray-500 dark:text-gray-400 mb-2">
          Conversations
        </h3>
        
        <button
          onClick={handleStartNewConversation}
          className="w-full mb-3 flex items-center justify-center p-2 rounded-2xl bg-blue-50 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-gray-600 text-blue-700 dark:text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Conversation
        </button>
        
        <ul className="space-y-2">
          {conversations.map(conversation => (
            <li key={conversation.id}>
              {editingId === conversation.id ? (
                <div className="flex w-full rounded-2xl p-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-none">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={handleTitleChange}
                    className="flex-1 p-1 text-sm rounded bg-transparent text-gray-700 dark:text-white"
                    autoFocus
                    onBlur={() => { if (!cancelRef.current) submitTitleEdit(conversation.id); cancelRef.current = false; }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitTitleEdit(conversation.id);
                      if (e.key === 'Escape') { cancelRef.current = true; setEditingId(null); }
                    }}
                  />
                </div>
              ) : (
                <div className="group flex items-center gap-1">
                  <button
                    onClick={() => handleSwitchToConversation(conversation.id)}
                    className={`flex-1 min-w-0 text-left px-2 py-1 rounded-2xl text-sm ${
                      conversation.id === currentSessionId
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-white'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-primary-text dark:text-gray-300'
                    }`}
                  >
                    <div className="truncate">{conversation.title}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      {formatDate(conversation.last_updated || conversation.created_at)}
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing(conversation.id, conversation.title);
                    }}
                    className="flex-shrink-0 p-1 rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition-opacity"
                    title="Edit title"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
        
        <button
          onClick={() => navigate('/conversations')}
          className="mt-2 text-xs text-apple-blue dark:text-apple-blue hover:text-blue-800 dark:hover:text-blue-300"
        >
          See all conversations →
        </button>
      </div>
    </div>
  );
}

export default ConversationList;
